import { useVisiblePlaceholders } from "@/hooks/use-placeholders";
import { useWalkPlaceholderPositions } from "@/hooks/use-walk-placeholder-positions";
import { useAppSelector } from "@/store/store-hooks";
import {
  selectObjectVisibility,
  selectVisibilityDistance,
} from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import {
  selectChildrenDepthFirst,
  useAssignClippingPlanes,
  useSvg,
} from "@faro-lotv/app-component-toolbox";
import {
  GUID,
  IElementGenericImgSheet,
  IElementImg360,
  isIElementImg360,
  isIElementOdometryPath,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { PathGeometry } from "@faro-lotv/lotv";
import { ThreeEvent } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  CatmullRomCurve3,
  DoubleSide,
  Mesh,
  Plane,
  RepeatWrapping,
  Texture,
  Vector3,
} from "three";
import { WaypointPosition } from "../waypoint-label-render";
import {
  ODOMETRY_PATH_OBJECT_NAME,
  PathWaypointTarget,
} from "./odometry-path-renderer";
import { selectTrajectoryWaypointPanoramas } from "./path-selectors";
import { useOdometryPathWalkPositions } from "./use-odometry-path-positions";
import PiastrellaSvg from "./VM_Piastrella.svg";
import PiastrellaSvgActive from "./VM_PiastrellaActive.svg";
import PiastrellaSvgHover from "./VM_PiastrellaHover.svg";
import { WalkPathPlaceholders } from "./walk-path-placeholder-renderer";

type WalkPathRendererProps = {
  /** The currently active panorama image. */
  activePano?: IElementImg360;

  /** The sheet to render the path for */
  sheetForElevation?: IElementGenericImgSheet;

  /** The section IElement containing the odometric path. */
  odometricPath: IElementGenericOdometryPath;

  /** Whether the odometric path is currently active. */
  isActive: boolean;

  /**
   * Whether the path fade-off (in the distance) is enabled.
   */
  fadeOff?: boolean;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Element that should be highlighted, e.g. with a hover effect */
  highlightedElement?: GUID;

  /**
   * Callback for when the path itself is clicked.
   *
   * @param odometricPath The odometric path being clicked.
   * @param point The point that is being clicked.
   * @param placeholderPoints Array containing the positions of all the placeholders
   */
  onPathClicked(
    odometricPath: IElementGenericOdometryPath,
    point: Vector3,
    placeholderPoints: Vector3[],
  ): void;

  /**
   * Callback for when a placeholder on the trajectory is clicked.
   *
   * @param target The waypoint being clicked.
   */
  onPlaceholderClicked(target: PathWaypointTarget): void;

  /** Callback called when the list of displayed waypoints has changed. */
  onWaypointsChanged(pathId: GUID, waypoints: WaypointPosition[]): void;
};

/** An offset to move the path below the placeholders so they don't z-fight */
const PATH_PLACEHOLDER_OFFSET = new Vector3(0, 0.01, 0);

/**
 * @returns The odometric path for a video mode capture series.
 */
export function WalkPathRenderer({
  activePano,
  sheetForElevation,
  odometricPath,
  isActive,
  fadeOff,
  clippingPlanes,
  highlightedElement,
  onPathClicked,
  onPlaceholderClicked,
  onWaypointsChanged,
}: WalkPathRendererProps): JSX.Element | null {
  const [isHovered, setIsHovered] = useState(false);

  // A path either groups the panoramas it was reconstructed from, or references an odometry file
  const odometryFilePath = isIElementOdometryPath(odometricPath)
    ? odometricPath
    : undefined;

  const panos = useAppSelector(
    selectChildrenDepthFirst(odometricPath, isIElementImg360),
    isEqual,
  );

  const panoPoints = useWalkPlaceholderPositions(panos, sheetForElevation);

  // Collect the trajectory 360s to render as waypoints: the ones not promoted to locations, plus
  // the first and last keyframe so the waypoints span the whole rendered path
  const odometryPanos = useAppSelector(
    selectTrajectoryWaypointPanoramas(odometricPath),
    isEqual,
  );

  // Compute the 3D position of the 360s not promoted to locations
  const odometryPoints = useWalkPlaceholderPositions(
    odometryPanos,
    sheetForElevation,
  );

  const filePoints = useOdometryPathWalkPositions(
    odometryFilePath,
    sheetForElevation,
  );

  const points = odometryFilePath ? filePoints : panoPoints;

  const placeholders = useMemo<Array<IElementImg360 | undefined>>(
    () => (odometryFilePath ? filePoints.map(() => undefined) : odometryPanos),
    [odometryFilePath, filePoints, odometryPanos],
  );

  const { visiblePlaceholders, visiblePositions } = useVisiblePlaceholders({
    placeholders,
    positions: odometryFilePath ? filePoints : odometryPoints,
    clippingPlanes,
  });

  const { visiblePositionsLocal, geometry } = useMemo(() => {
    // Offset the whole path by the first point location (then use it as group position)
    const visiblePositionsLocal = visiblePositions.map((p) =>
      p.clone().sub(points[0]),
    );
    if (points.length < 2) return { visiblePositionsLocal };

    // Make the path raise slowly at each point so overlapping path sections will not z-fight
    const heightStep = 0.01 / points.length;
    const heightStepPoint = new Vector3();
    const geometry = new PathGeometry(
      new CatmullRomCurve3(
        points.map((p, index) =>
          p
            .clone()
            .sub(points[0])
            .sub(PATH_PLACEHOLDER_OFFSET)
            .add(heightStepPoint.setY(heightStep * index)),
        ),
      ),
      { width: 0.1 },
    );
    return { visiblePositionsLocal, geometry };
  }, [points, visiblePositions]);

  const onRenderedWaypointsChanged = useCallback(
    (waypoints: WaypointPosition[]) => {
      onWaypointsChanged(odometricPath.id, waypoints);
    },
    [odometricPath.id, onWaypointsChanged],
  );

  // Notify the parent about the actual rendered positions of the placeholders
  useEffect(() => {
    onRenderedWaypointsChanged(
      panos.reduce<WaypointPosition[]>((array, pano, index) => {
        const renderPosition = points.at(index);
        // The positions are not immediately available on first render, so we skip them while they are being calculated
        if (renderPosition) {
          array.push({
            placeHolder: pano,
            renderPosition,
          });
        }
        return array;
      }, []),
    );
  }, [points, panos, onRenderedWaypointsChanged]);

  const textureDefault = useSvg(PiastrellaSvg);
  const textureHover = useSvg(PiastrellaSvgHover);
  const textureActive = useSvg(PiastrellaSvgActive);

  // Check if the path or one of its panos should be highlighted with a hover effect
  const isHighlighted = useMemo(
    () =>
      odometricPath.id === highlightedElement ||
      panos.some((p) => p.id === highlightedElement),
    [highlightedElement, odometricPath.id, panos],
  );

  let currentTexture: Texture | undefined;
  if (isActive) currentTexture = textureActive;
  else if (isHovered || isHighlighted) currentTexture = textureHover;
  else currentTexture = textureDefault;

  // Adjust the textures to the current curve
  useEffect(() => {
    for (const tex of [textureDefault, textureHover, textureActive]) {
      if (!tex) continue;
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;
      tex.generateMipmaps = false;
      tex.needsUpdate = true;
    }
  }, [geometry, textureDefault, textureHover, textureActive]);

  const visibilityDistance = useAppSelector(selectVisibilityDistance);

  const [pathRef, setPathRef] = useState<Mesh | null>(null);

  useAssignClippingPlanes(pathRef, clippingPlanes);

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) =>
      onPathClicked(odometricPath, ev.point, points),
    [odometricPath, onPathClicked, points],
  );

  const shouldTrajectoriesBeVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.trajectories),
  );

  const shouldWaypointsBeVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.waypoints),
  );

  return (
    <group
      position={points[0]}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {geometry && shouldTrajectoriesBeVisible && (
        <mesh
          name={ODOMETRY_PATH_OBJECT_NAME}
          ref={setPathRef}
          geometry={geometry}
          onClick={onClick}
        >
          <meshFadeOffMaterial
            transparent
            side={DoubleSide}
            map={currentTexture}
            // TODO: Add toggle to material to disable fade off (https://faro01.atlassian.net/browse/SWEB-1965)
            // eslint-disable-next-line react/no-unknown-property
            endFadeDistance={fadeOff ? visibilityDistance : Infinity}
            // eslint-disable-next-line react/no-unknown-property
            startFadeDistance={0}
            depthWrite={false}
          />
        </mesh>
      )}
      {(isActive || isHovered || isHighlighted) && shouldWaypointsBeVisible && (
        <WalkPathPlaceholders
          activeId={
            activePano
              ? visiblePlaceholders.findIndex((p) => p?.id === activePano.id)
              : undefined
          }
          hoveredId={
            highlightedElement
              ? visiblePlaceholders.findIndex(
                  (el) => el?.id === highlightedElement,
                )
              : undefined
          }
          placeholders={visiblePositionsLocal}
          fadeOff={fadeOff}
          shouldHighlightActive={fadeOff}
          onPlaceholderClick={(id) => {
            onPlaceholderClicked({
              path: odometricPath,
              position: visiblePositions[id],
              element: visiblePlaceholders[id],
            });
          }}
          onPlaceholderHovered={(hoveredElement) =>
            setIsHovered(!!hoveredElement)
          }
        />
      )}
    </group>
  );
}
